import React, { FC, useMemo } from "react";

import { EditableCard, Grid } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";

import { companyKeys } from "@/constants/CQueryKeys";

import type { TSection } from "@/types";
import { TCompanyBilling, TCompanyBillingUpdate } from "@/types";

import { promiseToast } from "@/modules/index";

import { COMPANY_BILLING_SECTIONS } from "../CCompanyBillingSections";

interface ICompanyBillingTabProps {
	companyId: number;
	canEdit: boolean;
}

const NUMBER_FIELDS = new Set(["defaultVatRate", "paymentTermsDays"]);
const BOOLEAN_FIELDS = new Set(["vatApplicable"]);

/**
 * The invoicing settings of a company, fetched when the tab opens (the
 * identity tab never needs them). One card, one PUT: the endpoint
 * replaces the whole record, so the card's fields are merged over what
 * was last read before sending.
 */
export const CompanyBillingTab: FC<ICompanyBillingTabProps> = ({
	companyId,
	canEdit,
}) => {
	const queryClient = useQueryClient();
	const sections = useMemo(() => COMPANY_BILLING_SECTIONS(), []);

	const { data: billing, isLoading } = useQuery({
		queryKey: companyKeys.billing(companyId),
		queryFn: () => CompaniesService.fetchBilling(companyId),
	});

	const handleSubmit = (section: TSection) => (values: object) => {
		if (!billing) return Promise.resolve();
		const record = values as Record<string, unknown>;
		const patch: Record<string, unknown> = {};
		for (const field of section.fields) {
			if (!(field.name in record)) continue;
			const value = record[field.name];
			patch[field.name] = NUMBER_FIELDS.has(field.name)
				? value === "" || value === null || value === undefined
					? null
					: Number(value)
				: BOOLEAN_FIELDS.has(field.name)
					? !!value
					: value;
		}
		const body: TCompanyBillingUpdate = {
			...toUpdate(billing),
			...patch,
		};
		const prom = CompaniesService.updateBilling(companyId, body).then(
			(saved) => {
				queryClient.setQueryData(companyKeys.billing(companyId), saved);
			}
		);
		promiseToast(prom);
		return prom;
	};

	// The cards want strings for the inputs, numbers (or null) for the
	// number fields, booleans for the toggle.
	const formValues =
		billing &&
		Object.fromEntries(
			sections
				.flatMap((section) => section.fields)
				.map((field) => {
					const value = (billing as Record<string, unknown>)[
						field.name
					];
					if (NUMBER_FIELDS.has(field.name))
						return [field.name, value ?? null];
					if (BOOLEAN_FIELDS.has(field.name))
						return [field.name, !!value];
					return [field.name, value ?? ""];
				})
		);

	return (
		<Grid columns={2} minItemWidth="400px">
			{sections.map((section) => (
				<EditableCard
					key={section.key}
					title={section.title}
					className="w-full"
					fields={section.fields}
					values={formValues}
					validationSchema={section.validationSchema}
					onSubmit={handleSubmit(section)}
					editInModal
					formikWrapper
					readOnly={!canEdit || isLoading}
					editColumns={1}
					modalProps={{ size: "md" }}
					editLabel={t("actions.edit")}
					submitLabel={t("actions.save")}
					cancelLabel={t("actions.cancel")}
				/>
			))}
		</Grid>
	);
};

const toUpdate = (billing: TCompanyBilling): TCompanyBillingUpdate => ({
	iban: billing.iban ?? "",
	bic: billing.bic ?? "",
	invoicePrefix: billing.invoicePrefix ?? "",
	vatApplicable: billing.vatApplicable,
	defaultVatRate: billing.defaultVatRate,
	paymentTermsDays: billing.paymentTermsDays,
	paymentTerms: billing.paymentTerms ?? "",
	legalMentions: billing.legalMentions ?? "",
	defaultCurrency: billing.defaultCurrency,
});
